import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import styled from "styled-components";
import { Box, Flex, Text } from "sprout-ui-primitives";
import { useCallback, useMemo } from "react";
import { ACTIONS, useActionRegistryGlobalHandler, useNavigationListContext } from "@sprout/list-navigation";
import { Button } from "sprout-ui-dom/dist/Button";
import { Link, useRouteMatch, useHistory } from "react-router-dom";
import RenderIfChildExists from "@sprout/client-components/dist/RenderIfChildExists";
import { topicPageRoutes } from "@sprout/client-routes";
import useSpecialTopic from "@sprout/hooks/dist/useSpecialTopic";
import AppHelpMenuKeyboardShortcutsGroup from "./AppHelpMenuKeyboardShortcutsGroup";
const NAVIGATION_ACTIONS = [
    {
        command: "OPEN_JUMP_TO",
        label: "SPROUT menu"
    },
    {
        command: "GO_HOME",
        label: "Home"
    },
    {
        command: "GO_AGENDA",
        label: "Agenda"
    },
    {
        command: "GO_MY_TASKS",
        label: "To-dos"
    },
    {
        command: "GO_ACTIVITY",
        label: "Activity"
    },
    {
        command: "GO_NOTIFICATIONS",
        label: "Notifications"
    },
    {
        command: "SEARCH",
        label: "Search"
    },
    {
        label: "Next item",
        keybinding: [
            "J",
            "↓"
        ]
    },
    {
        label: "Previous item",
        keybinding: [
            "K",
            "↑"
        ]
    },
    {
        command: "OPEN",
        label: "Open item"
    },
    {
        command: "GO_BACK",
        label: "Go back a page"
    },
    {
        command: "SHORTCUTS",
        label: "Show shortcuts"
    }, 
];
const POST_ACTIONS = [
    {
        command: "WHEN",
        label: "When"
    },
    {
        command: "DEADLINE",
        label: "Deadline"
    },
    {
        command: "ASSIGN",
        label: "Assign"
    },
    {
        command: "SET_COMPLETED",
        label: "Mark Done"
    },
    {
        command: "LABEL",
        label: "Label"
    },
    {
        command: "MOVE",
        label: "Move"
    },
    {
        command: "ARCHIVE",
        label: "Archive"
    },
    {
        command: "DELETE",
        label: "Delete"
    },
    {
        command: "EDIT_POST_DESCRIPTION",
        label: "Edit"
    },
    {
        command: "OPEN_EXTERNAL",
        label: "Open media"
    },
    {
        label: "Select Leaf",
        keybinding: "X"
    }, 
];
const POST_PAGE_ACTIONS = [
    {
        command: "ADD_TODO",
        label: "Add leaf"
    },
    {
        command: "ADD_TODOLIST",
        label: "Add list"
    }, 
];
const POST_PAGE_POST_ACTIONS = [
    {
        command: "PAGE_WHEN",
        label: "When"
    },
    {
        command: "PAGE_DEADLINE",
        label: "Deadline"
    },
    {
        command: "PAGE_ASSIGN",
        label: "Assign"
    },
    {
        command: "PAGE_TOGGLE_COMPLETED",
        label: "Mark Done"
    },
    {
        command: "PAGE_LABEL",
        label: "Label"
    },
    {
        command: "PAGE_MOVE",
        label: "Move"
    },
    {
        command: "PAGE_TOGGLE_ARCHIVE",
        label: "Archive"
    },
    {
        command: "PAGE_TOGGLE_DELETE",
        label: "Delete"
    },
    {
        command: "PAGE_FOCUS_DESCRIPTION",
        label: "Edit"
    },
    {
        command: "PAGE_OPEN_EXTERNAL",
        label: "Open media"
    }, 
];
const TOPIC_ACTIONS = [
    {
        command: "ADD_POST",
        label: "Add leaf"
    },
    {
        command: "ADD_SECTION",
        label: "Add branch"
    }, 
];
const INBOX_ACTIONS = [
    {
        command: "ADD_POST",
        label: "Add leaf"
    }, 
];
const StyledBox = styled(Box)`
  > div + div {
    margin-top: 24px;
  }
`;
const AppHelpMenuKeyboardShortcuts = ()=>{
    const { currentlyActiveCommands , dispatchCommand  } = useNavigationListContext();
    /**
   * This is needed because some actions overlapped with `TopicPage`,
   * namely `ADD_POST`, `ADD_DIVIDER`, this happens because of
   * `useBaseActionGlobalHandlers` usage.
   */ const { readLaterURL  } = useSpecialTopic();
    const readLaterPageRouteMatch = useRouteMatch({
        exact: true,
        path: readLaterURL
    });
    /**
   * This is needed because some actions overlapped with `PostPage`,
   * namely `PAGE_ARCHIVE` and `FOCUS_NOTE`.
   */ const topicPageRouteMatch = useRouteMatch({
        exact: true,
        path: topicPageRoutes
    });
    const postPageRouteMatch = useRouteMatch({
        exact: true,
        path: "/leaf/:postId"
    });
    const getActionsToDisplay = useCallback((actions)=>{
        return actions.reduce((acc, currentValue)=>{
            if ("keybinding" in currentValue) acc.push({
                label: currentValue.label,
                keybinding: currentValue.keybinding
            });
            else {
                const exists = currentlyActiveCommands.find((command)=>command === currentValue.command);
                if (exists) {
                    const match = ACTIONS.find((action)=>action.command === currentValue.command);
                    if (match) acc.push({
                        label: currentValue.label,
                        keybinding: match.keybindingForDisplay
                    });
                }
            }
            return acc;
        }, []);
    }, [
        currentlyActiveCommands
    ]);
    const activeNavigationActions = useMemo(()=>{
        return getActionsToDisplay(NAVIGATION_ACTIONS);
    }, [
        getActionsToDisplay
    ]);
    const activePostActions = useMemo(()=>{
        return getActionsToDisplay(POST_ACTIONS);
    }, [
        getActionsToDisplay
    ]);
    const activePostPageActions = useMemo(()=>{
        return getActionsToDisplay(POST_PAGE_ACTIONS);
    }, [
        getActionsToDisplay
    ]);
    const activePostPagePostActions = useMemo(()=>{
        return getActionsToDisplay(POST_PAGE_POST_ACTIONS);
    }, [
        getActionsToDisplay
    ]);
    const activeTopicActions = useMemo(()=>{
        return getActionsToDisplay(TOPIC_ACTIONS);
    }, [
        getActionsToDisplay
    ]);
    const activeInboxActions = useMemo(()=>{
        return getActionsToDisplay(INBOX_ACTIONS);
    }, [
        getActionsToDisplay
    ]);
    const history = useHistory();
    useActionRegistryGlobalHandler("PAGE_ENTER", useCallback(()=>{
        history.push("/shortcuts");
        dispatchCommand("ESCAPE", null);
        return true;
    }, [
        dispatchCommand,
        history
    ]));
    return /*#__PURE__*/ _jsxs(Box, {
        px: 5,
        children: [
            /*#__PURE__*/ _jsx(Text, {
                fontSize: 3,
                fontWeight: "bold",
                color: "white.100",
                mb: 4,
                children: "Keyboard Shortcuts"
            }),
            /*#__PURE__*/ _jsxs(Flex, {
                children: [
                    /*#__PURE__*/ _jsx(Box, {
                        minWidth: "280px",
                        children: /*#__PURE__*/ _jsx(AppHelpMenuKeyboardShortcutsGroup, {
                            shortcuts: activeNavigationActions,
                            groupTitle: "Navigation"
                        })
                    }),
                    /*#__PURE__*/ _jsxs(RenderIfChildExists, {
                        component: /*#__PURE__*/ _jsx(StyledBox, {
                            minWidth: "280px",
                            ml: 5
                        }),
                        children: [
                            activeTopicActions.length !== 0 && !readLaterPageRouteMatch && /*#__PURE__*/ _jsx(AppHelpMenuKeyboardShortcutsGroup, {
                                shortcuts: activeTopicActions,
                                groupTitle: "Tree"
                            }),
                            activePostActions.length !== 1 && !postPageRouteMatch && /*#__PURE__*/ _jsx(AppHelpMenuKeyboardShortcutsGroup, {
                                shortcuts: activePostActions,
                                groupTitle: "Leaf Item"
                            }),
                            activePostPageActions.length !== 0 && !topicPageRouteMatch && /*#__PURE__*/ _jsx(AppHelpMenuKeyboardShortcutsGroup, {
                                shortcuts: activePostPageActions,
                                groupTitle: "Leaf Item"
                            }),
                            activePostPagePostActions.length !== 0 && !topicPageRouteMatch && /*#__PURE__*/ _jsx(AppHelpMenuKeyboardShortcutsGroup, {
                                shortcuts: activePostPagePostActions,
                                groupTitle: "Leaf Item"
                            }),
                            activeInboxActions.length !== 0 && readLaterPageRouteMatch && /*#__PURE__*/ _jsx(AppHelpMenuKeyboardShortcutsGroup, {
                                shortcuts: activeInboxActions,
                                groupTitle: "Read Later"
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(Flex, {
                justifyContent: "flex-end",
                pt: 5,
                pb: 0,
                children: /*#__PURE__*/ _jsx(Button, {
                    variant: "bright-teal",
                    as: Link,
                    to: "/shortcuts",
                    children: "View all keyboard shortcuts"
                })
            })
        ]
    });
};
export default AppHelpMenuKeyboardShortcuts;
